// InfiniteMasonry.jsx
import {
    useState,
    useEffect,
    useMemo,
    useRef
} from 'react'
import { useTheme } from '@mui/material/styles'

import Masonry from 'react-masonry-css'
import Box from '@mui/material/Box'

const STEP = 25

export default function InfiniteMasonry({
    items,
    renderItem,
    columns = 3,
    spacing = 1
}) {
    const [visibleCount, setVisibleCount] = useState(STEP)
    const observer = useRef(null)
    const theme = useTheme()

    const gap = theme.spacing(spacing)

    useEffect(() => {
        setVisibleCount(STEP)
    }, [items])

    const visibleItems = useMemo(
        () => items.slice(0, visibleCount),
        [items, visibleCount]
    )

    const lastItemRef = node => {
        if (observer.current) observer.current.disconnect()

        if (!node) return

        observer.current = new IntersectionObserver(
            entries => {
                if (
                    entries[0].isIntersecting &&
                    visibleCount < items.length
                ) {
                    setVisibleCount(prev =>
                        Math.min(prev + STEP, items.length)
                    )
                }
            },
            {
                rootMargin: '300px',
            }
        )

        observer.current.observe(node)
    }

    return (
        <Box
            sx={{
                '& .my-masonry-grid': {
                    display: 'flex',
                    marginLeft: `-${gap}`,
                    width: 'auto',
                },
                '& .my-masonry-grid_column': {
                    paddingLeft: gap,
                    backgroundClip: 'padding-box',
                },
                '& .my-masonry-grid_column > div': {
                    marginBottom: gap,
                },
            }}
        >
            <Masonry
                breakpointCols={columns}
                className='my-masonry-grid'
                columnClassName='my-masonry-grid_column'
            >
                {visibleItems.map((item, index) =>
                    <div key={item.id || index}>
                        {renderItem(
                            item,
                            index === visibleItems.length - 1,
                            lastItemRef
                        )}
                    </div>
                )}
            </Masonry>
        </Box>
    )
}